import React, { useState } from 'react';
import { BannerSet, BannerSize } from '../types';
import { X, Download, FileText, Image as ImageIcon, CheckSquare, Square, Globe, Target, Zap } from 'lucide-react';
import { exportBannerSet } from '../utils/exportUtils';
import { useToast } from '../contexts/ToastContext';

interface PlatformExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  bannerSet: BannerSet;
}

const PLATFORMS = [
  {
    id: 'google-ads',
    name: 'Google Ads / Display Network',
    description: 'Google\'s advertising platform with massive reach',
    icon: Globe,
    formats: ['HTML5', 'PNG', 'JPEG', 'GIF'],
    maxFileSize: '150KB (HTML5), 5MB (Images)',
    requirements: [
      'HTML5 banners must be under 150KB',
      'Images should be high quality',
      'Include click tracking placeholder',
      'Responsive design recommended'
    ],
    color: 'blue',
    difficulty: 'Easy' as const
  },
  {
    id: 'facebook-meta',
    name: 'Meta (Facebook/Instagram)',
    description: 'Social media advertising across Facebook and Instagram',
    icon: Target,
    formats: ['PNG', 'JPEG'],
    maxFileSize: '30MB',
    requirements: [
      'Minimum 1080x1080 for feed posts',
      'Text should be minimal (20% rule relaxed)',
      'High contrast and clear imagery',
      'Mobile-first design approach'
    ],
    color: 'indigo',
    difficulty: 'Easy' as const
  },
  {
    id: 'trade-desk',
    name: 'The Trade Desk',
    description: 'Programmatic advertising platform for agencies',
    icon: Zap,
    formats: ['HTML5', 'PNG', 'JPEG', 'GIF'],
    maxFileSize: '200KB (HTML5), 10MB (Images)',
    requirements: [
      'HTML5 preferred for rich media',
      'Include TTD click macro: ${CLICK_URL}',
      'Support for video and interactive elements',
      'Cross-device compatibility'
    ],
    color: 'purple',
    difficulty: 'Medium' as const
  }
];

export const PlatformExportModal: React.FC<PlatformExportModalProps> = ({
  isOpen,
  onClose,
  bannerSet
}) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<'html' | 'png' | 'jpeg'>('html');
  const [quality, setQuality] = useState(90);
  const [isExporting, setIsExporting] = useState(false);
  
  const { showSuccess, showError, showInfo } = useToast();

  const completedSizes = bannerSet.sizes.filter(size => size.elements.length > 0);

  React.useEffect(() => {
    if (completedSizes.length > 0 && selectedSizes.length === 0) {
      setSelectedSizes(completedSizes.map(size => size.id));
    }
  }, [completedSizes, selectedSizes.length]);

  if (!isOpen) return null;

  const handlePlatformToggle = (platformId: string) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const handleSizeToggle = (sizeId: string) => {
    setSelectedSizes(prev => 
      prev.includes(sizeId) 
        ? prev.filter(id => id !== sizeId)
        : [...prev, sizeId]
    );
  };

  const handleExport = async () => {
    if (selectedPlatforms.length === 0) {
      showError('Export Error', 'Please select at least one platform.');
      return;
    }

    if (selectedSizes.length === 0) {
      showError('Export Error', 'Please select at least one banner size to export.');
      return;
    }

    if (bannerSet.dataFeedArray.length === 0) {
      showError('Export Error', 'Please upload a data feed first to export banners.');
      return;
    }

    setIsExporting(true);
    
    try {
      showInfo('Export Started', `Generating platform-optimized files for ${selectedPlatforms.length} platform${selectedPlatforms.length !== 1 ? 's' : ''}...`);
      
      const selectedSizeObjects = completedSizes.filter(size => selectedSizes.includes(size.id));
      const selectedPlatformNames = PLATFORMS.filter(platform => selectedPlatforms.includes(platform.id)).map(p => p.name);
      
      await exportBannerSet({
        bannerSet,
        selectedSizes: selectedSizeObjects,
        format: selectedFormat,
        quality: selectedFormat === 'jpeg' ? quality : undefined,
        platforms: selectedPlatformNames
      });

      showSuccess('Export Complete', `Successfully exported platform-optimized files for ${selectedPlatforms.length} platform${selectedPlatforms.length !== 1 ? 's' : ''}.`);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      showError('Export Failed', 'An error occurred while exporting the platform-optimized files. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const formatOptions = [
    { value: 'html', label: 'HTML5', icon: FileText, description: 'Interactive banners (recommended)', extension: '.html' },
    { value: 'png', label: 'PNG Images', icon: ImageIcon, description: 'High quality static images', extension: '.png' },
    { value: 'jpeg', label: 'JPEG Images', icon: ImageIcon, description: 'Compressed static images', extension: '.jpg' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Platform-Optimized Export</h2>
            <p className="text-sm text-gray-600 mt-1">Export banners optimized for specific advertising platforms</p>
          </div>
          <button onClick={onClose} disabled={isExporting} className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Platform Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Select Advertising Platforms</h3>
              <button
                onClick={() => setSelectedPlatforms(selectedPlatforms.length === PLATFORMS.length ? [] : PLATFORMS.map(p => p.id))}
                disabled={isExporting}
                className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
              >
                {selectedPlatforms.length === PLATFORMS.length ? <CheckSquare size={16} /> : <Square size={16} />}
                <span>{selectedPlatforms.length === PLATFORMS.length ? 'Deselect All' : 'Select All'}</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PLATFORMS.map((platform) => (
                <label
                  key={platform.id}
                  className={`flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedPlatforms.includes(platform.id) ? `border-${platform.color}-500 bg-${platform.color}-50` : ''
                  } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPlatforms.includes(platform.id)}
                    onChange={() => handlePlatformToggle(platform.id)}
                    disabled={isExporting}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <platform.icon size={24} className={selectedPlatforms.includes(platform.id) ? 'text-blue-600' : 'text-gray-500'} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">{platform.name}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        platform.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                        platform.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {platform.difficulty}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{platform.description}</p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div><strong>Formats:</strong> {platform.formats.join(', ')}</div>
                      <div><strong>Max Size:</strong> {platform.maxFileSize}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Export Format Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Export Format</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {formatOptions.map((format) => (
                <button
                  key={format.value}
                  onClick={() => setSelectedFormat(format.value as any)}
                  disabled={isExporting}
                  className={`p-4 border rounded-lg text-left transition-colors disabled:opacity-50 ${
                    selectedFormat === format.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <format.icon size={20} className={selectedFormat === format.value ? 'text-blue-600' : 'text-gray-500'} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{format.label}</span>
                        <span className="text-xs text-gray-500">{format.extension}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{format.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Quality Setting for JPEG */}
          {selectedFormat === 'jpeg' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Image Quality</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Quality: {quality}%</label>
                  <span className="text-xs text-gray-500">{quality >= 90 ? 'High' : quality >= 70 ? 'Medium' : 'Low'}</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={quality}
                  onChange={(e) => setQuality(parseInt(e.target.value))}
                  disabled={isExporting}
                  className="w-full disabled:opacity-50"
                />
              </div>
            </div>
          )}

          {/* Banner Size Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Select Banner Sizes</h3>
              <button
                onClick={() => setSelectedSizes(selectedSizes.length === completedSizes.length ? [] : completedSizes.map(size => size.id))}
                disabled={isExporting}
                className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
              >
                {selectedSizes.length === completedSizes.length ? <CheckSquare size={16} /> : <Square size={16} />}
                <span>{selectedSizes.length === completedSizes.length ? 'Deselect All' : 'Select All'}</span>
              </button>
            </div>

            <div className="space-y-2 max-h-48 overflow-y-auto">
              {completedSizes.map((size) => (
                <label
                  key={size.id}
                  className={`flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
                    isExporting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSizes.includes(size.id)}
                    onChange={() => handleSizeToggle(size.id)}
                    disabled={isExporting}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{size.name}</span>
                      <span className="text-sm text-gray-500">{size.width} × {size.height}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {size.elements.length} elements • {size.category} • {bannerSet.dataFeedArray.length} variations
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {completedSizes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>No completed banner sizes available for export.</p>
                <p className="text-sm mt-1">Please design at least one banner first.</p>
              </div>
            )}
          </div>

          {/* Export Summary */}
          {selectedPlatforms.length > 0 && selectedSizes.length > 0 && bannerSet.dataFeedArray.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Export Summary</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <div>Selected platforms: {selectedPlatforms.length} of {PLATFORMS.length}</div>
                <div>Selected sizes: {selectedSizes.length} of {completedSizes.length}</div>
                <div>Data variations: {bannerSet.dataFeedArray.length}</div>
                <div className="font-medium">Total files to export: {selectedPlatforms.length * selectedSizes.length * bannerSet.dataFeedArray.length}</div>
                <div className="text-xs text-blue-600 mt-2">
                  Files will be organized by platform with upload instructions and requirements.
                </div>
              </div>
            </div>
          )}

          {bannerSet.dataFeedArray.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-1">No Data Feed</h4>
              <p className="text-sm text-yellow-700">
                Please upload a data feed to enable platform-optimized export functionality.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {isExporting ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Generating platform-optimized files...</span>
              </div>
            ) : selectedPlatforms.length > 0 && selectedSizes.length > 0 && bannerSet.dataFeedArray.length > 0 ? (
              `Ready to export ${selectedPlatforms.length * selectedSizes.length * bannerSet.dataFeedArray.length} optimized files`
            ) : (
              'Select platforms, sizes, and ensure data feed is uploaded'
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              disabled={isExporting}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={selectedPlatforms.length === 0 || selectedSizes.length === 0 || bannerSet.dataFeedArray.length === 0 || isExporting}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={16} />
              <span>{isExporting ? 'Exporting...' : 'Export for Platforms'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};